import styles from './styles.module.scss';

import { useEffect, useMemo, useRef, useState } from 'react';

import { animate, motion, useDragControls, useMotionValue } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faDiamond, faFilter, faGripLines, faMagnifyingGlass } from '@fortawesome/free-solid-svg-icons';

import MapWithListingData from '../../../Misc/MapWithListingData/MapWithListingData';

import { PuffLoader, ScaleLoader } from 'react-spinners';
import Image from 'next/image';
import Head from 'next/head';
import errorMsg from '../../../Functions/errorMsg';
import axios from 'axios';

import returnCreatedTimeStamp from '../../../Functions/timestamps/returnCreatedTimeStamp';

import JobListing from '../../../Account/JobManager/JobListing/JobListing';

import Filter from '../../../Filters/Filter';

import formatCurrency from '../../../Functions/formatCurrency'
import { useRouter } from 'next/router';
export default function Jobs() {
  const router = useRouter();
  // Loading state variables
  const [mapLoading, setMapLoading] = useState(true);
  const [dataLoading, setDataLoading] = useState(true);

  const [listingData, setListingData] = useState(null);
  const [selectedData, setSelectedData] = useState([]);
  const [showingData, setShowingData] = useState([]);

  const [availableObjects, setAvailableObjects] = useState(null);

  useEffect(() => {
    if(!dataLoading || listingData || availableObjects) return;

    async function getListingData() {
      const result = await axios.get(`${window.location.origin}/api/jobs/general/jobListings`).catch((error) => errorMsg(error?.response?.data?.status || error.toString()))

      if(result && result.status === 200) {
        const data = result.data;
        setListingData(data.listings);
        setAvailableObjects(data.availableObjects)
      }
    }

    getListingData();
    setDataLoading(false);

  }, [dataLoading, listingData, availableObjects])

  useEffect(() => {
    if(!listingData) return;
    if(listingData?.length === 0) return;
    if(router?.query?.popup === 'sharedPost') {
      const getListingID = router.query._id;
      showClickedListing(getListingID)
    }
  }, [router, listingData])

   // Map Interaction Click Handler
  // =============================
  const [searchText, setSearchText] = useState('');
  const [selectedObjects, setSelectedObjects] = useState([]);

  useEffect(() => {
    if(!listingData) return;
    if(searchText.length > 0) {
      const filteredListings = listingData.filter((listing) => {
        const searchTextCheck = searchText.trim().toLowerCase();

        const businessTitle = listing.businessTitle.trim().toLowerCase();
        const positionTitle = listing.positionTitle.trim().toLowerCase();
        const positionType = listing.positionType.trim().toLowerCase();
    
        return (
          businessTitle.includes(searchTextCheck) ||
          positionTitle.includes(searchTextCheck) ||
          positionType.includes(searchTextCheck)
        );
      });

      setSelectedData(filteredListings);
      setShowingData(filteredListings);
    } else {
      const filteredListings = listingData.filter((listing) => selectedObjects.includes(listing.zip));
      setSelectedData(filteredListings);
      setShowingData(filteredListings);
    }
  }, [listingData, searchText])

  useEffect(() => {
    if (selectedObjects.length > 0) {
      const filteredListings = listingData.filter((listing) => selectedObjects.includes(listing.zip));
      setSelectedData(filteredListings);
      setShowingData(filteredListings);
    } else {
      if(searchText.length > 0) {
        const filteredListings = listingData.filter((listing) => {
          const searchTextCheck = searchText.trim().toLowerCase();
          
          const businessTitle = listing.businessTitle.trim().toLowerCase();
          const positionTitle = listing.positionTitle.trim().toLowerCase();
          const positionType = listing.positionType.trim().toLowerCase();
      
          return (
            businessTitle.includes(searchTextCheck) ||
            positionTitle.includes(searchTextCheck) ||
            positionType.includes(searchTextCheck)
          );
        });
  
        setSelectedData(filteredListings);
        setShowingData(filteredListings);
      } else {
        setSelectedData([]);
        setShowingData([]);
      }
    }
  }, [selectedObjects, listingData, searchText]);

  function handleObjectClick(obj) {
    setSelectedObjects(prevObjects => {
      if (prevObjects.includes(obj)) {
        return prevObjects.filter((curObj) => curObj !== obj);
      } else {
        return [...prevObjects, obj];
      }
    });
  }

  function isLoading() {
    if(mapLoading || dataLoading) return true;
    return false;
  }

  // FILTER STUFF ======================

  const [filterBar, setFilterBar] = useState(false);

  const showFilterBar = () => {
    if(isLoading()) return;
    setFilterBar(prevFilterBar => !prevFilterBar);
  }

  function filterApplyArray(newArray) {
    setShowingData(newArray);
  }

  function clearFilter() {
    setShowingData(selectedData);
  }

  // =============================
  // =============================

  const [clickedListing, showClickedListing] = useState(null);

  const memoizeListingContainer = useMemo(() => <ListingContainer noListings={listingData?.length === 0} listings={showingData} showFilterBar={showFilterBar} handleSearchMethod={(text) => setSearchText(text)} handleOpenListing={(listingID) => showClickedListing(listingID)} /> , [showingData, listingData]);
  const memoizeMap = useMemo(() => <MapWithListingData type='ZIP' availableObjects={availableObjects} handleMapLoaded={() => setMapLoading(false) } handleClick={handleObjectClick} /> , [listingData, availableObjects])

  return (
    <section id={styles.wrapper}>

      { clickedListing && <JobListing listingID={clickedListing} closeListing={() => showClickedListing(null)} /> }
      <Filter isLoading={isLoading()} filterType="Jobs" unfilteredData={selectedData} allResults={showingData} filterBar={filterBar} showFilterBar={showFilterBar} filterApplyArray={filterApplyArray} clearFilter={clearFilter} />
      <Head>
        <title>Waco Local Job Marketplace</title>
        <meta name="description" content="Let's create your next job listing with Waco Exchange! We offer a simple yet effective system to browse and post jobs locally in Waco and the surrounding communities." key="desc" />

        <meta property="og:title" content="Waco Local Job Marketplace" />
        <meta property="og:description" content="Let's create your next job listing with Waco Exchange! We offer a simple yet effective system to browse and post jobs locally in Waco and the surrounding communities." />
      </Head>
      
      { isLoading() && <div id={styles.loading}> <ScaleLoader width={50} height={40} color='white' /> </div> }

      <section id={styles.content}>
        { listingData && <h1 id={styles.mapTypeText}>Jobs Map</h1> }
        { listingData && memoizeMap}
        { listingData && memoizeListingContainer}

        { clickedListing && <JobListing listingID={clickedListing} closeListing={() => showClickedListing(null)} /> }
      </section>

    </section>
  )
}
const ListingContainer = ({ listings, showFilterBar, handleSearchMethod, handleOpenListing, noListings }) => {

  // Handling Listing Container Drag Controls & Animation
  // ====================================================
  const [showDisplayed, setShowDisplayed] = useState(false);
  const controls = useDragControls();
  const y = useMotionValue(showDisplayed ? 0 : 400);

  const onClickHandle = () => {
    if(showDisplayed) {
      setShowDisplayed(false)
      animate(y, 400, { type: 'tween', stiffness: 1000, damping: 10, bounce: 0 });
    } else {
      setShowDisplayed(true);
      animate(y, 0, { type: 'tween', stiffness: 1000, damping: 10, bounce: 0 });
    }
  }

  const onDragEnd = (_, info) => {
    const velocity = info.velocity.y;
    if (velocity > 0) {
      setShowDisplayed(false);
      animate(y, 400, { type: 'tween', stiffness: 1000, damping: 10, bounce: 0 });
    } else if (velocity < 0) {
      setShowDisplayed(true);
      animate(y, 0, { type: 'tween', stiffness: 1000, damping: 10, bounce: 0 });
    }
  };
  // ====================================================
  // ====================================================

  const displayContainerRef = useRef(null);

  const [showToTop, setShowToTop] = useState(false);

  const handleScroll = () => {
    if (!displayContainerRef || !displayContainerRef?.current) return;
    const position = displayContainerRef.current.scrollTop;

    if(position > 200) {
      setShowToTop(true);
    } else {
      setShowToTop(false);
    }

  };

  function handleScrollUp(e) {
    e.preventDefault();
    if (!displayContainerRef || !displayContainerRef?.current) return;
    displayContainerRef.current.scroll({top: 0, behavior: 'smooth' })
  }

  useEffect(() => {
    if(!displayContainerRef || !displayContainerRef?.current) return;

      displayContainerRef.current.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      if (!displayContainerRef || !displayContainerRef?.current) return;
      displayContainerRef.current.removeEventListener('scroll', handleScroll);
    };
  }, [displayContainerRef]);

  // Search Methods:

  const searchInputRef = useRef(null);

  function handleSearch() {
    if(!searchInputRef) return;
    if(!searchInputRef?.current) return;

    const text = searchInputRef.current.value;

    handleSearchMethod(text);
  }

  return (
    <motion.section id={styles.viewListings} style={{ y }} drag='y' dragElastic={0} dragConstraints={{ top: 0, bottom: 400 }} dragListener={false} dragControls={controls} onDragEnd={onDragEnd} >
      <div>
        <FontAwesomeIcon onClick={onClickHandle} icon={faGripLines} id={styles.handleContainerIcon} size='lg' onPointerDown={(e) => controls.start(e) } />
      </div>
      
      <div ref={displayContainerRef} id={styles.displayContainer}>
        { showToTop && <div id={styles.toTop}> <FontAwesomeIcon onClick={handleScrollUp} icon={faChevronUp} id={styles.icon} /> </div> }

        <section id={styles.queryMethods}>
          <div>
            <input ref={searchInputRef} placeholder='Search...' />
            <div onClick={handleSearch}> <FontAwesomeIcon icon={faMagnifyingGlass} id={styles.searchButton} /> </div>
          </div>
          <div>
            <FontAwesomeIcon icon={faFilter} id={styles.filter} onClick={showFilterBar} />
          </div>
          <div>
          </div>
        </section>

        {listings.length > 0 && listings.map((listing, index) => <DisplayCard key={index} listing={listing} handleOpenListing={handleOpenListing} />)}
        { noListings && listings.length === 0 && <h1>No Listings</h1> }
        { !noListings && listings.length === 0 && <h1>Select Map</h1> }

      </div>
    </motion.section>
  )
}

const DisplayCard = ({ listing, handleOpenListing }) => {

  const listingID = listing._id;

  const employerName = listing.businessTitle;
  const employerLogo = listing.businessLogo;
  const phoneNumber = listing.phoneNumber
  const [logoLoading, setLogoLoading] = useState(true);

  const positionTitle = listing.positionTitle;
  const positionType = listing.positionType;

  const { displayString } = returnCreatedTimeStamp(listing.creationDate);
  
  function returnPayType() {
    const rateData = listing.rate;
    if(rateData.type === 'Hourly') return `${formatCurrency(rateData.rate)} / hr`
    if(rateData.type === 'Salary') return `${formatCurrency(rateData.rate)} / year`
  }

  const { justPosted, urgentlyHiring, goodPay, excellentPay } = returnTagBooleans(listing);

  return (
    <div className={styles.displayCard} onClick={() => handleOpenListing(listingID)}>
      <section>
        <div>
          <h1>{positionTitle}</h1>
          <h2>{positionType}</h2>
          <h3>Posted {displayString}</h3>
        </div>
        <div>
          <h1>{returnPayType()}</h1>
          <section>
            <span id={styles.justPosted} style={{display: justPosted ? 'flex' : 'none'}}>Just posted</span>
            <span id={styles.urgentlyHiring} style={{display: urgentlyHiring ? 'flex' : 'none'}}>Urgently Hiring</span>
            <span id={styles.goodPay} style={{display: goodPay ? 'flex' : 'none'}}>Good Pay</span>
            <span id={styles.excellentPay} style={{display: excellentPay ? 'flex' : 'none'}}><FontAwesomeIcon icon={faDiamond} id={styles.icon} />Excellent Pay</span>
          </section>
        </div>
      </section>
      <section>
        <div>
          { logoLoading && <PuffLoader size={20} color='#818181' /> }
          <Image onLoadingComplete={() => setLogoLoading(false)} sizes='auto auto' fill objectFit='cover' src={employerLogo} alt={employerLogo} />
        </div>
        <div>
          <h1>{employerName}</h1>
          <h2>{phoneNumber}</h2>
        </div>

      </section>
    </div>
  )
}

function returnTagBooleans(listing) {
  const { justPosted } = returnCreatedTimeStamp(listing.creationDate);
  const rate = listing.rate;
  var urgentlyHiring = listing.urgentlyHiring;
  var goodPay = false;
  var excellentPay = false;

  if(rate.type === 'Hourly') {
    const hourlyPayRate = parseFloat(rate.rate);
    if(hourlyPayRate >= 20 && hourlyPayRate <= 30) {
      goodPay = true;
    }
    if(hourlyPayRate > 30) {
      excellentPay = true;
    }
  }

  if(rate.type === 'Salary') {
    const salaryPayRate = parseFloat(rate.rate);
    if(salaryPayRate >= 35000 && salaryPayRate <= 50000) {
      goodPay = true;
    }
    if(salaryPayRate > 50000) {
      excellentPay = true;
    }
  }


  return { justPosted, urgentlyHiring, goodPay, excellentPay }
}